import { useCallback, useRef, useState } from 'react';
import { getOrderStockItemLabels } from '../clients/stock_item_scan_client';
import { mapLabelsFetchError, mapPrintError } from './directLabelPrintErrors';
import {
  buildOrderLabelBatch,
  buildPrintJobName,
} from '../services/godexLabelBuilder';
import {
  connect,
  findPrinter,
  listPrinters,
  printRaw,
} from '../services/qzPrinterService';
import { resolvePrinterName } from '../utils/labelPrinterMatch';
import {
  getSavedPrinterName,
  setSavedPrinterName,
} from '../utils/labelPrinterStorage';
import { logInfo } from '../config_and_helpers/logger';

const LOG_CONTEXT = 'useDirectLabelPrint';

/**
 * Hook for QZ direct label printing workflow.
 * @param {string|null} authToken
 */
export const useDirectLabelPrint = authToken => {
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);

  const fetchLabels = useCallback(
    async orderId => {
      if (!authToken || !orderId) {
        return { success: false, message: 'Unable to prepare labels.' };
      }
      const result = await getOrderStockItemLabels(authToken, orderId);
      if (!result.success) {
        return { success: false, message: mapLabelsFetchError(result), status: result.status };
      }
      const labels = result.data?.labels || [];
      if (!labels.length) {
        return {
          success: false,
          message: 'No stock-item labels are available for this order.',
        };
      }
      return {
        success: true,
        labels,
        orderId: result.data?.order_id ?? orderId,
      };
    },
    [authToken]
  );

  const resolvePrinter = useCallback(async () => {
    const printers = await listPrinters(authToken);
    const saved = getSavedPrinterName();
    const resolved = resolvePrinterName(printers, saved);
    if (resolved) return { printerName: resolved, printers, needsSelection: false };
    return { printerName: saved, printers, needsSelection: true };
  }, [authToken]);

  const printLabels = useCallback(
    async (orderId, labels, printerName) => {
      if (busyRef.current) {
        return { success: false, message: 'Print already in progress.' };
      }
      busyRef.current = true;
      setBusy(true);
      try {
        await connect(authToken);
        const printers = await listPrinters(authToken);
        const resolved = findPrinter(printerName, printers);
        const ezpl = buildOrderLabelBatch(labels);
        const jobName = buildPrintJobName(orderId, labels);
        await printRaw(resolved, ezpl, jobName, authToken);
        setSavedPrinterName(resolved);
        logInfo(LOG_CONTEXT, 'print complete', {
          orderId,
          count: labels.length,
          printerName: resolved,
        });
        return {
          success: true,
          printerName: resolved,
          count: labels.length,
          message: `${labels.length} labels sent to ${resolved}`,
        };
      } catch (error) {
        const mapped = mapPrintError(error);
        return { success: false, ...mapped };
      } finally {
        busyRef.current = false;
        setBusy(false);
      }
    },
    [authToken]
  );

  return {
    busy,
    fetchLabels,
    resolvePrinter,
    printLabels,
    getSavedPrinterName,
    setSavedPrinterName,
  };
};
